import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";
import { FORM_MAX_IMAGES, imageProblem, pastedName } from "../../../lib/tickets";
import { uploadFile, uploadErrorText } from "../../../lib/portalUpload";
import { apiErrorMessage } from "../../../lib/api";

/**
 * Screenshots chosen for a ticket (14.10.2026): pasted with Ctrl+V / ⌘V
 * anywhere in the form, dragged in, or picked from the disk. Kept in the
 * browser until the ticket exists; `uploadScreenshots` sends them then.
 */
export default function ScreenshotPicker({ files, onChange, max = FORM_MAX_IMAGES, pasteTarget }) {
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const counter = useRef(files.length);
  const latest = useRef(files);
  latest.current = files;

  // Previews live as object URLs; let them go when a file leaves the list.
  const [previews, setPreviews] = useState({});
  useEffect(() => {
    const next = {};
    files.forEach((f) => { next[f.id] = previews[f.id] || URL.createObjectURL(f.file); });
    Object.keys(previews).forEach((id) => { if (!next[id]) URL.revokeObjectURL(previews[id]); });
    setPreviews(next);
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => Object.values(previews).forEach((u) => URL.revokeObjectURL(u)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (list) => {
    const current = latest.current;
    const room = max - current.length;
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    if (room <= 0) { toast.error(`Poți adăuga cel mult ${max} capturi aici.`); return; }
    const accepted = [];
    incoming.forEach((raw) => {
      const problem = imageProblem(raw);
      if (problem) { toast.error(problem); return; }
      if (accepted.length >= room) return;
      counter.current += 1;
      const pasted = !raw.name || raw.name === "image.png";
      const file = pasted ? new File([raw], pastedName(raw, counter.current), { type: raw.type }) : raw;
      accepted.push({ id: `${Date.now()}-${counter.current}`, file });
    });
    if (incoming.length > room) toast.error(`Am păstrat primele ${room}: cel mult ${max} capturi aici.`);
    if (accepted.length) onChange([...current, ...accepted]);
  };

  // Paste anywhere in the form: text goes where it was typed, images come here.
  useEffect(() => {
    const target = pasteTarget?.current;
    if (!target) return undefined;
    const onPaste = (e) => {
      const images = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
      if (images.length) { e.preventDefault(); add(images); }
    };
    target.addEventListener("paste", onPaste);
    return () => target.removeEventListener("paste", onPaste);
  }, [pasteTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer?.files); }}
        className={`rounded-xl border-2 border-dashed px-4 py-3 text-xs text-center transition-colors
          ${over ? "border-[#965EA7] bg-[#965EA7]/5" : "border-[#E5E7EB] bg-[#F5F6F6]/60"}`}
        data-testid="ticket-screenshot-drop"
      >
        <p className="text-[#373A45]">
          Lipește o captură cu <kbd className="font-sans font-semibold">Ctrl+V</kbd> / <kbd className="font-sans font-semibold">⌘V</kbd>,
          trage imaginea aici sau{" "}
          <button type="button" onClick={() => input.current?.click()} className="font-semibold text-[#7A4986] underline">
            alege din calculator
          </button>.
        </p>
        <p className="mt-1 text-[#6B7280]">PNG, JPG, WEBP sau GIF, cel mult 5 MB fiecare și {max} capturi.</p>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple className="sr-only"
          onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="ticket-screenshot-input" />
      </div>

      {files.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2" data-testid="ticket-screenshot-list">
          {files.map((f) => (
            <li key={f.id} className="relative">
              {previews[f.id] ? (
                <img src={previews[f.id]} alt={f.file.name} className="h-20 w-28 object-cover rounded-lg border border-[#E5E7EB] bg-white" />
              ) : (
                <div className="h-20 w-28 rounded-lg border border-[#E5E7EB] bg-white" />
              )}
              <button type="button" onClick={() => onChange(files.filter((x) => x.id !== f.id))}
                className="absolute -top-1.5 -right-1.5 rounded-full bg-[#373A45] text-white p-0.5 hover:bg-[#9b1f2c]"
                aria-label={`Scoate ${f.file.name}`}>
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The warning above the screenshots: they are kept for good. */
export function ScreenshotWarning() {
  return (
    <p className="text-[11px] leading-relaxed text-[#8a5a00] bg-[#F5B841]/10 rounded-lg px-3 py-2 mb-2 inline-flex gap-1.5">
      <ImagePlus size={14} className="shrink-0 mt-px" />
      <span>
        Nu pune în capturi CNP-uri sau date de card. Dacă bugul e la o anume înscriere, e suficient numele; o găsim noi.
        Capturile rămân în tichet.
      </span>
    </p>
  );
}

/** Send chosen screenshots to a ticket, one after another — with `commentId`,
 * as part of that message; from the portal, `base` is "/portal/tickets".
 * Returns how many failed. */
export async function uploadScreenshots(ticketId, files, { commentId, base = "/admin/tickets" } = {}) {
  let failed = 0;
  for (const { file } of files) {
    try {
      await uploadFile(`${base}/${ticketId}/attachments/upload-url`, file,
        { completeBase: `${base}/${ticketId}/attachments`, extra: commentId ? { comment_id: commentId } : {} });
    } catch (err) {
      failed += 1;
      toast.error(uploadErrorText(err, apiErrorMessage));
    }
  }
  return failed;
}
